import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Globe2, Ticket, Disc3 } from "lucide-react";

const CATALOG = [
  { phase: "START", value: 20 },
  { phase: "DEV", value: 140 },
  { phase: "A&R", value: 420 },
  { phase: "DATA", value: 950 },
  { phase: "SCALE", value: 1600 },
  { phase: "NOW", value: 2400 },
];

const STATS = [
  { icon: TrendingUp, value: "$20K → $2.4M+", label: "CATALOG VALUE GROWN", id: "catalog" },
  { icon: Disc3, value: "100%", label: "ARTISTS CHARTED AFTER SIGNING WITH US", id: "charted" },
  { icon: Globe2, value: "INTL", label: "INTERNATIONAL CHART PLACEMENTS", id: "international" },
  { icon: Ticket, value: "SECURED", label: "TOURING OPPORTUNITIES", id: "touring" },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111115] border border-[#22222B] px-4 py-3">
      <p className="mono text-[10px] tracking-[0.25em] text-[#60606E]">{label}</p>
      <p className="mono text-sm text-[#00F0FF]">${payload[0].value}K</p>
    </div>
  );
};

export default function TrackRecord() {
  return (
    <section id="track-record" className="relative py-28 sm:py-36 bg-[#0C0C10] border-y border-[#22222B]" data-testid="track-record-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <p className="mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-4" data-testid="track-record-eyebrow">
              TRACK RECORD // RECEIPTS
            </p>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="track-record-heading">
              THE PROOF IS IN<br />THE <span className="text-[#CCFF00]">PRESSING</span>
            </h2>
          </div>
          <p className="mono text-[11px] tracking-[0.2em] text-[#60606E] max-w-xs leading-relaxed">
            CATALOG VALUATION TRAJECTORY ($K) — FROM ROUGHLY $20,000 TO MULTIMILLION-DOLLAR OPPORTUNITIES.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-px bg-[#22222B]">
          <div className="grid grid-cols-2 gap-px bg-[#22222B]">
            {STATS.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="bg-[#08080A] p-6 sm:p-8 flex flex-col justify-between min-h-[170px] group hover:bg-[#111115] transition-colors duration-500"
                data-testid={`stat-${s.id}`}
              >
                <s.icon className="h-5 w-5 text-[#00F0FF]" />
                <div>
                  <p className="font-display text-2xl sm:text-3xl text-[#F2F2F5] group-hover:text-[#CCFF00] transition-colors duration-500">
                    {s.value}
                  </p>
                  <p className="mono text-[9px] tracking-[0.2em] text-[#60606E] mt-2 leading-relaxed">{s.label}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="bg-[#08080A] p-6 sm:p-10 tech-corner"
            data-testid="catalog-chart"
          >
            <p className="mono text-[10px] tracking-[0.3em] text-[#60606E] mb-8">FIG. 01 — CATALOG GROWTH CURVE</p>
            <div className="h-[260px] sm:h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={CATALOG} margin={{ top: 5, right: 5, bottom: 0, left: -18 }}>
                  <defs>
                    <linearGradient id="catalogFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1A1A24" vertical={false} />
                  <XAxis
                    dataKey="phase"
                    stroke="#60606E"
                    tick={{ fill: "#60606E", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                    tickLine={false}
                    axisLine={{ stroke: "#22222B" }}
                  />
                  <YAxis
                    stroke="#60606E"
                    tick={{ fill: "#60606E", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${v}K`}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#00F0FF", strokeOpacity: 0.3 }} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#00F0FF"
                    strokeWidth={2}
                    fill="url(#catalogFill)"
                    dot={{ r: 3, fill: "#CCFF00", strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: "#CCFF00", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mono text-[11px] tracking-[0.25em] text-[#A0A0AB] mt-12 text-center"
          data-testid="track-record-charted-line"
        >
          EVERY ARTIST WE FOUND WAS UNCHARTED BEFORE US. <span className="text-[#CCFF00]">EVERY ONE CHARTED AFTER.</span>
        </motion.p>

        <div className="mt-20">
          <p className="mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-8" data-testid="receipts-eyebrow">
            THE RECEIPTS // SCREENSHOTS OR IT DIDN'T HAPPEN
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { img: "/assets/floyd-milestone.jpg", artist: "FLOYD WEST22", text: "'ON REPEAT (RADIO EDIT)' — SPOTIFY TECH HOUSE 2024, POSITION 6", date: "JUL 2024", id: "floyd" },
              { img: "/assets/sixluu-milestone.jpg", artist: "6LU_U", text: "'NO HOPE FOR ME WITHOUT YOU' — #5 ITUNES GERMANY CHRISTIAN & GOSPEL", date: "NOV 2023", id: "6luu" },
              { img: "/assets/kaput-milestone.jpg", artist: "KAPUT", text: "SPOTIFY POPULARITY INDEX 23 AND CLIMBING", date: "MAY 2024", id: "kaput" },
              { img: "/assets/skippy-rankings.jpg", artist: "SKY WOODS", text: "GLOBAL RANKINGS ACROSS 10+ GENRES — DANCE, HIP-HOP/RAP, ELECTRONIC", date: "CHARTMETRIC", id: "skywoods" },
            ].map((r) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7 }}
                className="group border border-[#22222B] bg-[#08080A] overflow-hidden"
                data-testid={`receipt-${r.id}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={r.img}
                    alt={`${r.artist} chart receipt`}
                    loading="lazy"
                    className="h-full w-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-4">
                  <p className="mono text-[10px] tracking-[0.2em] text-[#CCFF00]">{r.artist}</p>
                  <p className="mono text-[9px] tracking-[0.1em] text-[#A0A0AB] mt-2 leading-relaxed">{r.text}</p>
                  <p className="mono text-[9px] tracking-[0.2em] text-[#60606E] mt-2">{r.date}</p>
                </div>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="border border-[#00F0FF]/40 bg-[#08080A] p-4 flex flex-col justify-between tech-corner"
              data-testid="receipt-monstrdrop"
            >
              <p className="font-display text-5xl text-[#00F0FF]">#1</p>
              <div>
                <p className="mono text-[10px] tracking-[0.2em] text-[#CCFF00]">MONSTR DROP</p>
                <p className="mono text-[9px] tracking-[0.1em] text-[#A0A0AB] mt-2 leading-relaxed">
                  'PIMP HOUSE VOL. 12' — #1 ON THE COLA & LOLLIPOPS CHART
                </p>
                <p className="mono text-[9px] tracking-[0.2em] text-[#60606E] mt-2">CHART TOPPER</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
